import React, { useState, useEffect, useRef } from 'react';
import quoteCartService from '../../services/quoteCartService';
import './QuoteCart.css';

const QuoteCart = ({ isOpen, onClose, onCartUpdated }) => {
    const [cart, setCart] = useState({ items: [], itemCount: 0 });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [success, setSuccess] = useState(false);
    const [referenceNumber, setReferenceNumber] = useState('');
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        customerFirstName: '',
        customerLastName: '',
        customerEmail: '',
        customerPhone: '',
        companyName: '',
        zipPostalCode: '',
        country: 'United States',
        specialRequests: ''
    });

    // Track if component is mounted to prevent state updates after unmount
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadCart();
        }
    }, [isOpen]);

    const loadCart = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await quoteCartService.getCart();
            if (isMounted.current) {
                setCart(data || { items: [], itemCount: 0 });
            }
        } catch (err) {
            console.error('Failed to load cart:', err);
            if (isMounted.current) {
                setCart({ items: [], itemCount: 0 });
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleUpdateQuantity = async (itemId, newQuantity) => {
        try {
            await quoteCartService.updateItem(itemId, { quantity: newQuantity });
            if (isMounted.current) await loadCart();
            if (onCartUpdated) onCartUpdated();
        } catch (err) {
            if (isMounted.current) setError('Failed to update quantity');
        }
    };

    const handleRemoveItem = async (itemId) => {
        try {
            await quoteCartService.removeItem(itemId);
            if (isMounted.current) await loadCart();
            if (onCartUpdated) onCartUpdated();
        } catch (err) {
            if (isMounted.current) setError('Failed to remove item');
        }
    };

    const handleClearCart = async () => {
        if (!window.confirm('Are you sure you want to clear your Quote Folder?')) return;
        try {
            await quoteCartService.clearCart();
            if (isMounted.current) setCart({ items: [], itemCount: 0 });
            if (onCartUpdated) onCartUpdated();
        } catch (err) {
            if (isMounted.current) setError('Failed to clear cart');
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const result = await quoteCartService.submitCart(formData);
            if (isMounted.current) {
                setReferenceNumber(result.referenceNumber);
                setSuccess(true);
            }
            if (onCartUpdated) onCartUpdated();
        } catch (err) {
            if (isMounted.current) {
                setError(err.message || 'Failed to submit quote request');
            }
        } finally {
            if (isMounted.current) setSubmitting(false);
        }
    };

    const handleClose = () => {
        setShowSubmitForm(false);
        setSuccess(false);
        setError('');
        setReferenceNumber('');
        setFormData({
            customerFirstName: '',
            customerLastName: '',
            customerEmail: '',
            customerPhone: '',
            companyName: '',
            zipPostalCode: '',
            country: 'United States',
            specialRequests: ''
        });
        onClose();
    };

    const parseConfig = (configJson) => {
        try {
            return JSON.parse(configJson);
        } catch {
            return {};
        }
    };

    if (!isOpen) return null;

    return (
        <div className="quote-cart-overlay" onClick={handleClose}>
            <div className="quote-cart-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="quote-cart-header">
                    <h2>
                        {success ? 'Quote Submitted!' : showSubmitForm ? 'Submit Quote Request' : 'Quote Folder'}
                    </h2>
                    <button className="quote-cart-close" onClick={handleClose}>&times;</button>
                </div>

                {success ? (
                    <div className="quote-cart-success">
                        <div className="success-icon">✓</div>
                        <p>Your quote request has been submitted successfully.</p>
                        <p className="reference-number">Reference: <strong>{referenceNumber}</strong></p>
                        <p>We will contact you shortly with pricing information.</p>
                        <button className="quote-cart-btn primary" onClick={handleClose}>Close</button>
                    </div>
                ) : showSubmitForm ? (
                    <form onSubmit={handleSubmit} className="quote-cart-form">
                        <div className="quote-cart-body">
                            {error && <div className="quote-cart-error">{error}</div>}

                            <div className="quote-cart-summary">
                                <strong>{cart.items?.length || 0} item(s)</strong> in your quote
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name *</label>
                                    <input
                                        type="text"
                                        name="customerFirstName"
                                        value={formData.customerFirstName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name *</label>
                                    <input
                                        type="text"
                                        name="customerLastName"
                                        value={formData.customerLastName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        name="customerEmail"
                                        value={formData.customerEmail}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone *</label>
                                    <input
                                        type="tel"
                                        name="customerPhone"
                                        value={formData.customerPhone}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Company Name *</label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>ZIP/Postal Code *</label>
                                    <input
                                        type="text"
                                        name="zipPostalCode"
                                        value={formData.zipPostalCode}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Country *</label>
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="United States">United States</option>
                                        <option value="Canada">Canada</option>
                                        <option value="Mexico">Mexico</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Special Requests</label>
                                <textarea
                                    name="specialRequests"
                                    value={formData.specialRequests}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Any additional requirements or questions..."
                                />
                            </div>
                        </div>

                        <div className="quote-cart-footer">
                            <button
                                type="button"
                                className="quote-cart-btn secondary"
                                onClick={() => setShowSubmitForm(false)}
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                className="quote-cart-btn primary"
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting...' : 'Submit Quote Request'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="quote-cart-body">
                            {error && <div className="quote-cart-error">{error}</div>}

                            {loading ? (
                                <div className="quote-cart-loading">Loading cart...</div>
                            ) : !cart.items || cart.items.length === 0 ? (
                                <div className="quote-cart-empty">
                                    <div className="empty-icon">📂</div>
                                    <p>Your quote folder is empty</p>
                                    <p className="empty-hint">Add configured products to request a multi-item quote</p>
                                </div>
                            ) : (
                                <div className="quote-cart-items">
                                    {cart.items.map((item) => {
                                        const config = parseConfig(item.configurationJson);
                                        const configKeys = Object.keys(config).slice(0, 3);

                                        return (
                                            <div key={item.id} className="quote-cart-item">
                                                <div
                                                    className="item-thumbnail item-clickable"
                                                    onClick={() => setSelectedItem(item)}
                                                    title="View configuration"
                                                >
                                                    {item.thumbnailUrl ? (
                                                        <img src={item.thumbnailUrl} alt={item.productDisplayName} />
                                                    ) : (
                                                        <div className="no-thumb">📦</div>
                                                    )}
                                                </div>
                                                <div className="item-details">
                                                    <h4
                                                        className="item-name-clickable"
                                                        onClick={() => setSelectedItem(item)}
                                                        title="View configuration"
                                                    >
                                                        {item.productDisplayName || item.productName}
                                                    </h4>
                                                    {configKeys.length > 0 && (
                                                        <div className="item-config">
                                                            {configKeys.map(key => (
                                                                <span key={key} className="config-tag">
                                                                    {key}: {config[key]?.value || config[key]}
                                                                </span>
                                                            ))}
                                                            {Object.keys(config).length > 3 && (
                                                                <span
                                                                    className="config-more item-clickable"
                                                                    onClick={() => setSelectedItem(item)}
                                                                >
                                                                    +{Object.keys(config).length - 3} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="item-quantity">
                                                        <button
                                                            className="qty-btn"
                                                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                                            disabled={item.quantity <= 1}
                                                        >
                                                            -
                                                        </button>
                                                        <span className="qty-value">{item.quantity}</span>
                                                        <button
                                                            className="qty-btn"
                                                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                                <button
                                                    className="item-remove"
                                                    onClick={() => handleRemoveItem(item.id)}
                                                    title="Remove item"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="quote-cart-footer">
                            {cart.items && cart.items.length > 0 && (
                                <>
                                    <button
                                        className="quote-cart-btn text"
                                        onClick={handleClearCart}
                                    >
                                        Clear Folder
                                    </button>
                                    <button
                                        className="quote-cart-btn primary"
                                        onClick={() => setShowSubmitForm(true)}
                                    >
                                        Request Quote ({cart.itemCount} items)
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Configuration Summary Modal */}
            {selectedItem && (
                <div className="quote-config-overlay" onClick={() => setSelectedItem(null)}>
                    <div className="quote-config-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="quote-config-header">
                            <h3>Configuration Summary</h3>
                            <button className="quote-config-close" onClick={() => setSelectedItem(null)}>
                                &times;
                            </button>
                        </div>
                        <div className="quote-config-product">
                            {selectedItem.productDisplayName || selectedItem.productName}
                        </div>
                        <div className="quote-config-body">
                            {(() => {
                                const config = parseConfig(selectedItem.configurationJson);
                                const keys = Object.keys(config);
                                if (keys.length === 0) {
                                    return <div className="quote-config-empty">No configuration data</div>;
                                }
                                return (
                                    <table className="quote-config-table">
                                        <thead>
                                            <tr>
                                                <th>Parameter</th>
                                                <th>Value</th>
                                                <th>Units</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {keys.map(key => {
                                                const val = config[key];
                                                const value = val?.value !== undefined ? val.value : val;
                                                const units = val?.units && val.units !== 'Text' ? val.units : '-';
                                                return (
                                                    <tr key={key}>
                                                        <td>{key}</td>
                                                        <td className="quote-config-value">{String(value)}</td>
                                                        <td className="quote-config-units">{units}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuoteCart;
